import { create } from 'zustand';
import { Consultation } from '@/types';
import { supabase } from '@/lib/supabase';
import { trackChange } from '@/lib/utils/sync-tracker';
import { v4 as uuidv4 } from 'uuid';

interface ConsultationsState {
  consultations: Consultation[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: number;
  pendingUpdates: Set<string>;
  
  // Core operations
  fetchConsultations: () => Promise<void>;
  getConsultation: (id: string) => Consultation | undefined;
  getConsultationsByMasterPatient: (masterPatientId: string) => Consultation[];
  getConsultationsByPatient: (patientId: string) => Consultation[];
  
  // CRUD operations
  createConsultation: (consultation: {
    masterPatientId: string;
    patientId: string;
    reason: string;
    priority: 'normal' | 'urgent' | 'emergency';
    doctorId?: string;
    status?: 'waiting' | 'in-consultation' | 'completed' | 'no-show';
    registeredAt: string;
    notes?: string;
  }) => Promise<string>; // Returns consultation ID
  
  updateConsultation: (id: string, updates: Partial<Consultation>) => Promise<void>;
  deleteConsultation: (id: string) => Promise<void>;
  
  // Utility functions
  linkPatientToMaster: (patientId: string, masterPatientId: string) => Promise<void>;
}

// Cache functions
const loadConsultationsCache = (): Consultation[] => {
  try {
    const cached = localStorage.getItem('consultationsCache');
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Cache valid for 5 minutes
      if (Date.now() - timestamp < 5 * 60 * 1000) {
        return data;
      }
    }
  } catch (error) {
    console.warn('Failed to load consultations cache:', error);
  }
  return [];
};

const saveConsultationsCache = (consultations: Consultation[]) => {
  try {
    localStorage.setItem('consultationsCache', JSON.stringify({
      data: consultations,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.warn('Failed to save consultations cache:', error);
  }
};

export const useConsultationsStore = create<ConsultationsState>()((set, get) => ({
  consultations: loadConsultationsCache(),
  isLoading: false,
  error: null,
  lastUpdated: 0,
  pendingUpdates: new Set(),

  fetchConsultations: async () => {
    set({ isLoading: true, error: null });
    try {
      console.log('Fetching consultations...');
      const { data, error } = await supabase
        .from('consultations')
        .select('*')
        .order('registered_at', { ascending: false });

      if (error) {
        console.error('Supabase error fetching consultations:', error);
        throw error;
      }

      // Map snake_case to camelCase
      const mappedConsultations = (data || []).map(consultation => ({
        id: consultation.id,
        masterPatientId: consultation.master_patient_id,
        patientId: consultation.patient_id,
        reason: consultation.reason,
        priority: consultation.priority,
        doctorId: consultation.doctor_id,
        status: consultation.status,
        registeredAt: consultation.registered_at,
        completedAt: consultation.completed_at,
        completedBy: consultation.completed_by,
        followUpDate: consultation.follow_up_date,
        notes: consultation.notes,
        createdAt: consultation.created_at,
        updatedAt: consultation.updated_at
      }));

      console.log('Fetched consultations:', mappedConsultations);
      set({ consultations: mappedConsultations, isLoading: false, lastUpdated: Date.now() });
      saveConsultationsCache(mappedConsultations);
    } catch (error) {
      console.error('Error fetching consultations:', error);
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  getConsultation: (id: string) => {
    return get().consultations.find(c => c.id === id);
  },

  getConsultationsByMasterPatient: (masterPatientId: string) => {
    return get().consultations.filter(c => c.masterPatientId === masterPatientId);
  },

  getConsultationsByPatient: (patientId: string) => {
    return get().consultations.filter(c => c.patientId === patientId);
  },

  createConsultation: async (consultation) => {
    try {
      console.log('Creating consultation:', consultation);
      
      const now = new Date().toISOString();
      const consultationId = 'CONS-' + uuidv4();
      
      // Create optimistic consultation object
      const optimisticConsultation: Consultation = {
        id: consultationId,
        masterPatientId: consultation.masterPatientId,
        patientId: consultation.patientId,
        reason: consultation.reason,
        priority: consultation.priority,
        doctorId: consultation.doctorId,
        status: consultation.status || 'waiting',
        registeredAt: consultation.registeredAt,
        completedAt: undefined,
        completedBy: undefined,
        followUpDate: undefined,
        notes: consultation.notes || '',
        createdAt: now,
        updatedAt: now
      };

      // Optimistic update
      set(state => {
        const updated = [optimisticConsultation, ...state.consultations];
        saveConsultationsCache(updated);
        return { 
          consultations: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, consultationId])
        };
      });

      // Note: Consultation is created directly in database below, no need to track for sync

      // Backend sync
      const { data, error } = await supabase
        .from('consultations')
        .insert([{
          id: consultationId,
          master_patient_id: consultation.masterPatientId,
          patient_id: consultation.patientId,
          reason: consultation.reason,
          priority: consultation.priority,
          doctor_id: consultation.doctorId,
          status: consultation.status || 'waiting',
          registered_at: consultation.registeredAt,
          notes: consultation.notes || '',
          created_at: now,
          updated_at: now
        }])
        .select();

      if (error) {
        console.error('Supabase error creating consultation:', error);
        
        // Handle duplicate key error specifically
        if (error.code === '23505') {
          console.warn('Consultation ID collision detected, generating new ID and retrying...');
          
          // Generate new UUID-based ID
          const newConsultationId = 'CONS-' + uuidv4();
          
          // Update the consultation with new ID
          const updatedConsultation = { ...optimisticConsultation, id: newConsultationId };
          
          // Update optimistic state with new ID
          set(state => {
            const updated = state.consultations.map(c => 
              c.id === consultationId ? updatedConsultation : c
            );
            saveConsultationsCache(updated);
            return { 
              consultations: updated, 
              pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== consultationId).add(newConsultationId))
            };
          });
          
          // Retry with new ID
          const { data: retryData, error: retryError } = await supabase
            .from('consultations')
            .insert([{
              id: newConsultationId,
              master_patient_id: consultation.masterPatientId,
              patient_id: consultation.patientId,
              reason: consultation.reason,
              priority: consultation.priority,
              doctor_id: consultation.doctorId,
              status: consultation.status || 'waiting',
              registered_at: consultation.registeredAt,
              notes: consultation.notes || '',
              created_at: now,
              updated_at: now
            }])
            .select();
            
          if (retryError) {
            console.error('Retry failed:', retryError);
            // Rollback optimistic update
            set(state => {
              const updated = state.consultations.filter(c => c.id !== newConsultationId);
              saveConsultationsCache(updated);
              return { 
                consultations: updated, 
                pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== newConsultationId)),
                error: retryError.message
              };
            });
            throw retryError;
          }
          
          console.log('Consultation created successfully with new ID:', newConsultationId);
          return newConsultationId;
        }
        
        // Rollback optimistic update for other errors
        set(state => {
          const updated = state.consultations.filter(c => c.id !== consultationId);
          saveConsultationsCache(updated);
          return { 
            consultations: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== consultationId)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data
      const mappedConsultation = {
        id: data[0].id,
        masterPatientId: data[0].master_patient_id,
        patientId: data[0].patient_id,
        reason: data[0].reason,
        priority: data[0].priority,
        doctorId: data[0].doctor_id,
        status: data[0].status,
        registeredAt: data[0].registered_at,
        completedAt: data[0].completed_at,
        completedBy: data[0].completed_by,
        followUpDate: data[0].follow_up_date,
        notes: data[0].notes,
        createdAt: data[0].created_at,
        updatedAt: data[0].updated_at
      };

      set(state => {
        const updated = state.consultations.map(c => c.id === consultationId ? mappedConsultation : c);
        saveConsultationsCache(updated);
        return { 
          consultations: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(id => id !== consultationId))
        };
      });

      return consultationId;
    } catch (error) {
      console.error('Error creating consultation:', error);
      set({ error: (error as Error).message });
      throw error;
    }
  },

  updateConsultation: async (id, updates) => {
    try {
      const currentConsultation = get().consultations.find(c => c.id === id);
      if (!currentConsultation) throw new Error('Consultation not found');

      // Create optimistic update
      const optimisticConsultation: Consultation = {
        ...currentConsultation,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // Optimistic update
      set(state => {
        const updated = state.consultations.map(c => c.id === id ? optimisticConsultation : c);
        saveConsultationsCache(updated);
        return { 
          consultations: updated, 
          pendingUpdates: new Set([...state.pendingUpdates, id])
        };
      });

      // Track change for incremental sync
      trackChange({
        id,
        table: 'consultations',
        operation: 'update',
        data: {
          reason: updates.reason,
          priority: updates.priority,
          doctor_id: updates.doctorId,
          status: updates.status,
          completed_at: updates.completedAt,
          completed_by: updates.completedBy,
          follow_up_date: updates.followUpDate,
          notes: updates.notes,
          updated_at: optimisticConsultation.updatedAt
        },
        originalData: {
          reason: currentConsultation.reason,
          priority: currentConsultation.priority,
          doctor_id: currentConsultation.doctorId,
          status: currentConsultation.status,
          completed_at: currentConsultation.completedAt,
          completed_by: currentConsultation.completedBy,
          follow_up_date: currentConsultation.followUpDate,
          notes: currentConsultation.notes,
          updated_at: currentConsultation.updatedAt
        }
      });

      // Backend sync
      const { data, error } = await supabase
        .from('consultations')
        .update({
          reason: updates.reason,
          priority: updates.priority,
          doctor_id: updates.doctorId,
          status: updates.status,
          completed_at: updates.completedAt,
          completed_by: updates.completedBy,
          follow_up_date: updates.followUpDate,
          notes: updates.notes,
          updated_at: optimisticConsultation.updatedAt
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating consultation:', error);
        // Rollback optimistic update
        set(state => {
          const updated = state.consultations.map(c => c.id === id ? currentConsultation : c);
          saveConsultationsCache(updated);
          return { 
            consultations: updated, 
            pendingUpdates: new Set([...state.pendingUpdates].filter(cid => cid !== id)),
            error: error.message
          };
        });
        throw error;
      }

      // Update with server data
      const mappedConsultation = {
        id: data.id,
        masterPatientId: data.master_patient_id,
        patientId: data.patient_id,
        reason: data.reason,
        priority: data.priority,
        doctorId: data.doctor_id,
        status: data.status,
        registeredAt: data.registered_at,
        completedAt: data.completed_at,
        completedBy: data.completed_by,
        followUpDate: data.follow_up_date,
        notes: data.notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      set(state => {
        const updated = state.consultations.map(c => c.id === id ? mappedConsultation : c);
        saveConsultationsCache(updated);
        return { 
          consultations: updated, 
          pendingUpdates: new Set([...state.pendingUpdates].filter(cid => cid !== id))
        };
      });
    } catch (error) {
      console.error('Error updating consultation:', error);
      set({ error: (error as Error).message });
    }
  },

  deleteConsultation: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('consultations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => {
        const updated = state.consultations.filter(c => c.id !== id);
        saveConsultationsCache(updated);
        return { consultations: updated, isLoading: false };
      });
    } catch (error) {
      set({ error: (error as Error).message, isLoading: false });
    }
  },

  linkPatientToMaster: async (patientId: string, masterPatientId: string) => {
    try {
      console.log('Linking patient to master:', { patientId, masterPatientId });
      
      // Call the database function to link patient to master
      const { error } = await supabase.rpc('link_patient_to_master', {
        p_patient_id: patientId,
        p_master_patient_id: masterPatientId
      });

      if (error) {
        console.error('Supabase error linking patient to master:', error);
        throw error;
      }

      // Refresh the consultations list
      await get().fetchConsultations();
      
      console.log('Successfully linked patient to master');
    } catch (error) {
      console.error('Error linking patient to master:', error);
      set({ error: (error as Error).message });
      throw error;
    }
  }
})); 